import {Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormControl, Validators} from "@angular/forms";
import {ContentService} from "../../_services/content.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {videoLinkUpload} from '../../_utils/video-link-sanitizer';
import {PostAttachment} from '../../_types/content';
import {Heatmap, Poll} from '../../_types/polls';
import {AuthService} from '../../_services/auth.service';
import {UserAccessLevel} from '../../_types/communities';
import {PollsService} from '../../_services/polls.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../confirmation-dialog/confirmation-dialog.component';
import {ConfirmationDialogData} from '../../_types/shared';
import {SnackbarService} from '../../_services/snackbar.service';
import {CreatePollComponent} from '../create-poll/create-poll.component';
import {MatButtonToggleGroup} from "@angular/material/button-toggle";
import {LocalStorageService} from "../../_services/local-storage.service";
import imageCompression from "browser-image-compression";

@Component({
    selector: 'app-attachments',
    templateUrl: './attachments.component.html',
    styleUrl: './attachments.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class AttachmentsComponent {
    @ViewChild(CreatePollComponent, {static: false}) createPollComponent?: CreatePollComponent;
    @ViewChild('group', {static: false}) group!: MatButtonToggleGroup;
    @ViewChild('videoInput') videoInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('audioInput') audioInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('imageInput') imageInputRef!: ElementRef<HTMLInputElement>;

    @Input() attachmentOptions: string [] = ['text', 'image', 'video', 'audio', 'videoLink', 'screenRecording', 'doc', 'gif', 'survey_url', 'poll', 'heatmap']
    @Input() commentType: string = 'text'
    @Input() comment: boolean = false;
    @Input() displayOverText: boolean = false;
    @Input() small: boolean = false;
    @Input() hideTypeChoosing: boolean = false;
    @Input() reply: boolean = false;
    @Input() disable: boolean = false;
    @Output() isValid: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() imagePositionChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() submitField: EventEmitter<any> = new EventEmitter<any>();
    @Output() removedAttachment: EventEmitter<'poll' | 'heatmap'> = new EventEmitter<'poll' | 'heatmap'>();
    @Output() updatedPoll: EventEmitter<void> = new EventEmitter<void>();
    @Output() inUse: EventEmitter<boolean> = new EventEmitter<boolean>();
    compressableImageTypes: string[] = ['bmp', 'webp', 'png', 'jpeg', 'jpg']
    url: string = '(((ftp|http|https):\\/\\/)|(\\/)|(..\\/))(\\w+:{0,1}\\w*@)?(\\S+)(:[0-9]+)?(\\/|\\/([\\w#!:.?+=&%@!\\-\\/]))?';
    gifs: any[] = [];
    page: number = 1;
    limit: number = 15;
    resultsLength: number = 0;
    imageCtrl: FormControl<any> = new FormControl(null, Validators.required);
    image: any;
    videoCtrl: FormControl<any> = new FormControl(null, Validators.required);
    video: any;
    audioCtrl: FormControl<any> = new FormControl(null, Validators.required);
    audio: any;
    videoLinkFormControl: FormControl<string | null> = this.fb.control(null, [Validators.pattern(this.url), Validators.required]);
    linkFormControl: FormControl<any> = this.fb.control(null, [Validators.pattern(this.url), Validators.required]);
    surveyFormControl: FormControl<any> = this.fb.control(null, [Validators.pattern(this.url), Validators.required]);
    docTitle: FormControl<any> = this.fb.control(null);
    docCtrl: FormControl<any> = new FormControl(null, Validators.required);
    doc: any;
    gif: any;
    videoLink: any;
    fileSize: number = 0;
    maxFilesize: number = 100;
    recording: boolean = false;
    screenRecording: any;
    mediaRecorder: any;
    stream: any;
    screenRecordingUrl: any;
    isLoaded: boolean = true;
    initialFile: any;
    initialHeatmap?: Heatmap;
    initialPoll?: Poll;
    safeImageLink?: SafeResourceUrl;
    isEditPoll: boolean = false;
    heatMapEditMode: boolean = false;
    isApp: boolean = this.localStorageService.getItem('isApp', false);

    constructor(private readonly fb: FormBuilder, private readonly contentService: ContentService,
                private readonly snackBar: MatSnackBar,
                private domSanitizer: DomSanitizer, private authService: AuthService,
                private pollsService: PollsService, private dialog: MatDialog,
                private snackbarService: SnackbarService,
                private readonly localStorageService: LocalStorageService) {

        if (!this.attachmentOptions.includes(this.commentType)) {
            this.commentType = this.attachmentOptions[0];
        }
        this.imageCtrl.valueChanges.subscribe(async (selectedValue): Promise<void> => {
            this.image = null;
            if (!selectedValue) {
                return;
            }
            const mimeType = selectedValue?.type;
            if (mimeType && mimeType.match(/image\/*/) == null) {
                this.openSnackBar("Only images are supported.", 'error');
                return;
            }

            let compressedFile;
            if (this.hasFileType(mimeType, this.compressableImageTypes)) {
                try {
                    compressedFile = await imageCompression(selectedValue, {
                        maxWidthOrHeight: 3840,
                        useWebWorker: true,
                        fileType: 'image/jpeg'
                    });
                    compressedFile = new File([compressedFile], "compressed.jpeg", {type: 'image/jpeg'});
                    const savedKB: number = (selectedValue.size - compressedFile.size) / 1024;
                    console.log(`Kb Saved: ${savedKB} (${(savedKB / (selectedValue.size / 1024)) * 100}%)`);
                } catch (error) {
                    console.error(error);
                }
            }

            const reader: FileReader = new FileReader();
            reader.readAsDataURL(selectedValue);
            reader.onload = (_event): void => {
                this.image = reader.result;
            }


            this.fileSize = +(Math.round(+selectedValue.size / 1024) / 1000).toFixed(2)
            if (this.fileSize < this.maxFilesize) {
                this.isValid.emit(true);
                this.submitField.emit({
                    type: 'image',
                    value: compressedFile ? this.ensureFileNameIsCorrectSize(compressedFile) : this.ensureFileNameIsCorrectSize(selectedValue),
                    initial: selectedValue === this.initialFile
                })
            } else {
                this.image = null;
            }
        })


        this.videoCtrl.valueChanges.subscribe((selectedValue): void => {
            this.video = null;
            if (!selectedValue) {
                return;
            }
            const mimeType = selectedValue?.type;
            if (mimeType && mimeType?.match(/video\/*/) == null) {
                this.openSnackBar("Only videos are supported.", 'error');
                return;
            }

            const reader: FileReader = new FileReader();
            reader.readAsDataURL(selectedValue);
            reader.onload = (event): void => {
                this.video = (<FileReader>event.target).result;
            }
            this.fileSize = +(Math.round(+selectedValue.size / 1024) / 1000).toFixed(2)

            if (this.fileSize < this.maxFilesize) {
                this.isValid.emit(true);
                this.submitField.emit({
                    type: 'video',
                    value: this.ensureFileNameIsCorrectSize(selectedValue),
                    initial: selectedValue === this.initialFile
                })
            } else {
                this.video = null;
            }

        })
        this.audioCtrl.valueChanges.subscribe((selectedValue): void => {
            this.audio = null;
            if (!selectedValue) {
                return;
            }
            const mimeType = selectedValue?.type;
            if (mimeType && mimeType.match(/audio\/*/) == null) {
                this.openSnackBar("Only audio files are supported.", 'error');
                return;
            }

            const reader: FileReader = new FileReader();
            reader.readAsDataURL(selectedValue);
            reader.onload = (event): void => {
                this.audio = (<FileReader>event.target).result;
            }
            this.fileSize = +(Math.round(+selectedValue.size / 1024) / 1000).toFixed(2)
            if (this.fileSize < this.maxFilesize) {
                this.isValid.emit(true);
                this.submitField.emit({
                    type: 'audio',
                    value: this.ensureFileNameIsCorrectSize(selectedValue),
                    initial: selectedValue === this.initialFile
                })
            } else {
                this.audio = null;
            }
        })


        this.docCtrl.valueChanges.subscribe((selectedValue): void => {
            this.doc = selectedValue.name;
            this.fileSize = +(Math.round(+selectedValue.size / 1024) / 1000).toFixed(2)

            if (this.fileSize < this.maxFilesize) {
                this.isValid.emit(true);
            }
            this.submitField.emit({
                type: 'document',
                value: this.ensureFileNameIsCorrectSize(selectedValue),
                initial: selectedValue === this.initialFile
            })
        })

        this.surveyFormControl.valueChanges.subscribe((selectedValue): void => {
            this.submitField.emit({
                type: 'survey_url',
                value: selectedValue,
                initial: selectedValue === this.initialFile
            })
        })
    }

    @Input() set initialAttachment(value: PostAttachment | Heatmap | undefined | null) {
        if (!value?.id) return;
        this.storeInitialAttachmentForEditing(value);
    };

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    ensureFileNameIsCorrectSize(file: File): any {
        // API only allows files with less than 100 char filename
        let fileToSubmit: File = file;

        function getFileExtension(filename: string): string {
            const parts = filename.split('.');
            return parts.length > 1 ? `.${parts.pop()}` : '';
        }

        // Check if the filename length exceeds 100 characters
        if (file.name.length > 100) {
            const originalExtension: string = getFileExtension(file.name);
            const desiredFileName = `upload${originalExtension}`;
            // Create a new File object with the desired name
            fileToSubmit = new File([file], desiredFileName, {type: file.type});
        }
        return fileToSubmit;
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.contentService.userHasCommunityRole(roles);
    }

    onToggleGroupChange(event: any): void {
        this.inUse.emit(event.value !== 'text')
        this.fileSize = 0;
        this.isValid.emit(false);
        if (this.initialPoll && event.value && event.value != 'poll') {
            this.removePoll();
        } else if (this.initialHeatmap && event.value && event.value != 'heatmap') {
            this.removeHeatmap();
        }
        this.submitField.emit({type: event.value, value: null, initial: false});
        switch (event.value) {
            case 'image':
                this.checkValidity(this.imageCtrl, event.value);
                if (this.isApp) {
                    setTimeout((): void => {
                        this.imageInputRef?.nativeElement.click();
                    }, 100);
                }
                break;
            case 'video':
                this.checkValidity(this.videoCtrl, event.value);
                if (this.isApp) {
                    setTimeout((): void => {
                        this.videoInputRef?.nativeElement.click();
                    }, 100);
                }
                break;
            case 'audio':
                this.checkValidity(this.audioCtrl, event.value);
                if (this.isApp) {
                    setTimeout((): void => {
                        this.audioInputRef?.nativeElement.click();
                    }, 100);
                }
                break;
            case 'videoLink':
                this.checkValidity(this.videoLinkFormControl, 'videoLink')
                break;
            case 'screenRecording':
                this.checkValidity(this.videoLinkFormControl, 'screenRecording')
                break;
            case 'doc':
                this.checkValidity(this.docCtrl, 'document');
                break;
            case 'gif':
                if (this.gif) this.selectGif(this.gif);
                break;
        }
    }

    checkValidity(formControl: FormControl, fieldType: string): void {
        if (formControl.valid) {
            this.isValid.emit(true);
            this.submitField.emit({
                type: fieldType,
                value: formControl.value,
                initial: formControl.value === this.initialFile
            })
        } else {
            this.isValid.emit(false);
        }
    }

    searchGifs(searchTerm: string, page?: number): void {
        this.contentService.searchGifs(searchTerm, this.limit, this.page).subscribe(r => {
            this.gifs = r.data;
            this.resultsLength = r.pagination.total_count;

        }, e => {
            this.openSnackBar(e, 'error')
        })
    }

    handlePageEvent(event: any, search: string): void {
        this.page = event.pageIndex + 1;
        this.searchGifs(search, this.page);
    }

    selectGif(resultGif: any): void {
        this.isValid.emit(true);
        this.gif = resultGif;
        this.submitField.emit({
            type: 'gif',
            value: resultGif,
            initial: this.gif.embed_url === this.initialFile?.embed_url
        })
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }

    async startScreenRecording(audio?: boolean): Promise<void> {

        console.log(audio)
        if (audio) {
            this.stream = await this.recordScreenWithNarration()
        } else {
            this.stream = await this.recordScreen();
        }
        this.recording = true;
        this.mediaRecorder = this.createRecorder(this.stream);
    }

    stopScreenRecording(): void {
        this.recording = false;
        this.mediaRecorder.stop();
        this.stream?.getTracks() // get all tracks from the MediaStream
            .forEach((track: any) => track.stop()); // stop each of them
        this.stream = null;
    }

    async recordScreen(): Promise<MediaStream> {
        return await navigator.mediaDevices.getDisplayMedia({
            audio: true,
            video: {
                width: {max: 1920}, height: {max: 1080}
            },
            // @ts-ignore: selfBrowserSurface is not yet in DisplayMediaStreamOptions type definitions
            selfBrowserSurface: "include",
            systemAudio: "include",
            surfaceSwitching: "include",
            monitorTypeSurfaces: "include",
        });
    }

    async recordScreenWithNarration(): Promise<MediaStream> {
        let screenStream: MediaStream | null = null;
        let microphoneStream: MediaStream | null = null;
        let combinedStream: MediaStream | null = null;

        try {
            microphoneStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });

            if (!microphoneStream || microphoneStream.getAudioTracks().length === 0) {
                console.warn('Microphone stream acquired but no audio tracks found, or permission denied.');
                this.snackbarService.openErrorSnackBar("Microphone not detected or accessible. Please check your system settings and browser permissions.");
                throw new Error('Microphone access failed or no audio tracks available.');
            }
            screenStream = await navigator.mediaDevices.getDisplayMedia({
                audio: false,
                video: {
                    width: {max: 1920},
                    height: {max: 1080}
                },
                // @ts-ignore: selfBrowserSurface is not yet in DisplayMediaStreamOptions type definitions
                selfBrowserSurface: "include",
                surfaceSwitching: "include",
                monitorTypeSurfaces: "include",
            });

            if (!screenStream) {
                console.error('Screen stream could not be acquired.');
                throw new Error('Screen capture failed.');
            }
            combinedStream = new MediaStream();
            screenStream.getVideoTracks().forEach(track => combinedStream!.addTrack(track));
            // Add all audio tracks from the microphone stream
            microphoneStream.getAudioTracks().forEach(track => combinedStream!.addTrack(track));
            return combinedStream;
        } catch (error) {
            console.error("Error recording screen with narration (without system audio):", error);

            if (error instanceof DOMException) {
                if (error.name === "NotAllowedError") {
                    this.snackbarService.openErrorSnackBar("Permission denied. Please allow microphone and screen sharing access.");
                } else if (error.name === "NotFoundError") {
                    this.snackbarService.openErrorSnackBar("No microphone or screen found. Please ensure your devices are connected and configured.");
                } else if (error.name === "AbortError") {
                    this.snackbarService.openErrorSnackBar("Screen recording with narration was cancelled.");
                } else {
                    this.snackbarService.openErrorSnackBar(`Recording error: ${error.message || 'An unknown error occurred.'}`);
                }
            } else {
                this.snackbarService.openErrorSnackBar("Failed to start screen recording with narration. Please try again.");
            }
            screenStream?.getTracks().forEach(track => track.stop());
            microphoneStream?.getTracks().forEach(track => track.stop());
            throw error;
        }
    }

    createRecorder(stream: MediaStream): MediaRecorder {
        // the stream data is stored in this array
        let recordedChunks: Blob[] = [];

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorder.ondataavailable = function (e): void {
            if (e.data.size > 0) {
                recordedChunks.push(e.data);
            }
        };
        let that = this
        mediaRecorder.addEventListener('stop', event => {
            const blob: Blob = new Blob(recordedChunks, {
                type: 'video/mp4'
            });
            that.screenRecording = blob;
            that.screenRecordingUrl = URL.createObjectURL(blob);
            const file: File = new File([blob], `recording_${new Date().getTime()}.mp4`, {type: 'video/mp4'});
            this.fileSize = +(Math.round(+file.size / 1024) / 1000).toFixed(2)
            if (this.fileSize < this.maxFilesize) {
                this.isValid.emit(true);
            } else {
                this.openSnackBar("Screen recording is too large.", 'error');

            }
            this.submitField.emit({type: 'video', value: file});
            recordedChunks = [];
            this.stopScreenRecording();
        });

        mediaRecorder.start(200); // For every 200ms the stream data will be stored in a separate chunk.
        return mediaRecorder;
    }

    videoLinkUpload(link: string | null): void {
        if (link) {
            this.videoLink = videoLinkUpload(link, this.domSanitizer);
        }
    }

    editPoll(): void {
        this.isEditPoll = true;
    }

    pollUpdated(poll: Poll): void {
        this.initialPoll = poll;
        this.commentType = 'poll';
        this.isValid.emit(true);
        this.isEditPoll = false;
        this.updatedPoll.emit();
    }

    removePoll(): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to delete this Poll? <br>(<i>Please note</i>: all votes will also be deleted)',
                title: 'Delete poll',
                showHeader: true
            }
        });
        dialogRef.afterClosed().subscribe(r => {
            if (r) {
                this.pollsService.deletePoll(this.initialPoll!.id).subscribe(r => {
                        this.removedAttachment.emit('poll');
                        this.initialPoll = undefined;
                        this.commentType = 'text';
                        this.snackbarService.openSuccessSnackBar('Heatmap has been successfully deleted.');
                    },
                    e => {
                        this.snackbarService.openErrorSnackBar(e);
                    }
                )
            } else {
                this.commentType = 'poll'
            }
        })

    }

    removeHeatmap(): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to delete this Heatmap? <br>(<i>Please note</i>: all votes will also be deleted)',
                title: 'Delete heatmap',
                showHeader: true
            }
        });
        dialogRef.afterClosed().subscribe(r => {
            if (r) {
                this.pollsService.deleteHeatmap(this.initialHeatmap!.id).subscribe(r => {
                        this.removedAttachment.emit('heatmap');
                        this.initialHeatmap = undefined;
                        this.commentType = 'text';
                        this.snackbarService.openSuccessSnackBar('Heatmap has been successfully deleted.');
                    },
                    e => {
                        this.snackbarService.openErrorSnackBar(e);
                    })
            } else {
                this.commentType = 'heatmap';
            }
        })
    }

    reset() {
        this.submitField.emit({
            type: 'text',
            value: null,
            initial: !this.initialFile
        });
        this.isValid.emit(true);
    }

    changeHeatmapEndDate() {
        this.heatMapEditMode = true;
    }

    hasFileType(fileType: string, typeList: string[]): boolean {
        let included: boolean = false;
        for (let type of typeList) {
            if (fileType && fileType?.toLowerCase().includes(type)) {
                included = true;
            }
        }
        return included
    }

    private storeInitialAttachmentForEditing(value: PostAttachment | Heatmap): void {
        this.isValid.emit(false);
        this.isLoaded = false;
        const isPostAttachment = (v: PostAttachment | Heatmap | Poll): v is PostAttachment => {
            return !!(v as PostAttachment)?.attachment_type;
        }
        const isPoll = (v: PostAttachment | Heatmap | Poll): v is Poll => {
            return !!(v as Poll)?.questions;
        }
        if (isPostAttachment(value)) {
            if (value?.attachment_type === 'image' || value?.attachment_type === 'video' || value?.attachment_type === 'document' || value?.attachment_type === 'audio') {
                this.contentService.fetchFile(value?.file).then((res): void => {
                    this.initialFile = res;
                    this.commentType = value?.attachment_type;
                    switch (this.commentType) {
                        case 'image':
                            this.imageCtrl.setValue(res);
                            break;
                        case 'video':
                            this.videoCtrl.setValue(res);
                            break;
                        case 'audio':
                            this.audioCtrl.setValue(res);
                            break;
                        case 'document' || 'doc':
                            this.docCtrl.setValue(res);
                            const regex: RegExp = /\/posts\/attachments\/(.*)\?X-Amz-Algorithm=/;
                            const match: RegExpMatchArray | null = value.file.match(regex);
                            if (match) {
                                const dotIndex: number = match[1].indexOf('.');
                                this.docTitle.setValue(match[1].substring(0, dotIndex))
                            }
                            setTimeout((): void => {
                                this.commentType = 'doc'
                            });
                            break;
                    }
                    this.isLoaded = true;
                });
            } else if (value?.attachment_type === 'video_url') {
                this.initialFile = value.url;
                this.commentType = 'videoLink';
                this.videoLinkFormControl.setValue(value.url);
                this.checkValidity(this.videoLinkFormControl, 'videoLink')
                this.isLoaded = true;
            } else if (value?.attachment_type === 'gif_url') {
                this.initialFile = {embed_url: value.url};
                this.safeImageLink = videoLinkUpload(value.url, this.domSanitizer)?.link;
                this.commentType = 'gif';
                this.selectGif({embed_url: value.url});
                this.isLoaded = true;
            } else if (value?.attachment_type === 'survey_url' && value.url) {
                this.initialFile = {survey_url: value.url};
                // @ts-ignore
                this.surveyFormControl.setValue(value?.url);
                this.checkValidity(this.surveyFormControl, 'survey')
                this.safeImageLink = videoLinkUpload(value.url, this.domSanitizer)?.link;
                this.commentType = "survey_url";
                this.isLoaded = true;
            } else if (value?.attachment_type === 'url' && value.url) {
                this.initialFile = {url: value.url};
                // @ts-ignore
                this.surveyFormControl.setValue(value?.url);
                this.checkValidity(this.surveyFormControl, 'url')
                this.safeImageLink = videoLinkUpload(value.url, this.domSanitizer)?.link;
                this.commentType = "url";
                this.isLoaded = true;
            }

        } else if (!isPoll(value)) {
            this.initialHeatmap = value;
            this.commentType = 'heatmap';
            this.isValid.emit(true);
            this.isLoaded = true;
        } else {
            this.initialPoll = value;
            this.commentType = 'poll';
            this.onToggleGroupChange('poll')
            this.isValid.emit(true);
            this.isLoaded = true;
        }
    }
}


